/** Master-detail panel for one canonical config key: who defines it, with
 *  what values, and (with the manage permission) inline cross-tenant editing.
 *
 * Baseline occurrences come from the config index; pending edits overlay on
 * top so the panel always shows the EFFECTIVE state. Two read views — value
 * groups (few distinct values) or a per-tenant table — picked adaptively with
 * a manual override; edit mode always uses the table. Secret keys never group
 * by value (the mask sentinel makes value identity meaningless).
 */
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import type { TenantConfigItem } from '@/api/tourOperatorConfigs';
import { PermissionGate } from '@/components/PermissionGate';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';

import { safePreview } from '../config/schema-grid/schemaOps';

import { useBatchData } from './BatchDataContext';
import { UNSET, type PendingEdit } from './changeSet';
import { useChangeSet } from './ChangeSetContext';
import type { BatchRow } from './configIndex';
import { canonicalize } from './pathOps';
import { TenantValueTable } from './TenantValueTable';
import { ValueGroupSection, type ValueGroupItem } from './ValueGroupSection';

export interface EffectiveEntry {
  tenantId: number;
  concretePath: string;
  /** Effective value — pending edits applied over the baseline. */
  value: unknown;
  /** Baseline value; `undefined` = entry only exists as a pending add. */
  original: unknown;
  pending?: PendingEdit;
}

export interface RemovedEntry {
  tenantId: number;
  concretePath: string;
  /** Baseline value the pending UNSET removes. */
  original: unknown;
}

export function KeyDetailPanel({ row }: { row: BatchRow }) {
  const { index, tenantById, tenantCount } = useBatchData();
  const { state } = useChangeSet();
  const [editing, setEditing] = useState(false);
  const [viewOverride, setViewOverride] = useState<'grouped' | 'table' | null>(null);

  // Baseline occurrences overlaid with this key's pending edits. Depends on
  // `state.version` (the store's memo signal), not the state object identity.
  const { entries, removedEntries } = useMemo(() => {
    const map = new Map<string, EffectiveEntry>();
    for (const occ of index.byKey.get(row.id) ?? []) {
      map.set(`${occ.tenantId}:${occ.concretePath}`, {
        tenantId: occ.tenantId,
        concretePath: occ.concretePath,
        value: occ.value,
        original: occ.value,
      });
    }
    const removed: RemovedEntry[] = [];
    for (const [tidStr, tenantEdits] of Object.entries(state.edits)) {
      const tenantId = Number(tidStr);
      for (const [path, edit] of Object.entries(tenantEdits)) {
        let canonical: string;
        try {
          canonical = canonicalize(path);
        } catch {
          continue;
        }
        if (canonical !== row.id) continue;
        const key = `${tenantId}:${path}`;
        if (edit.after === UNSET) {
          map.delete(key);
          removed.push({ tenantId, concretePath: path, original: edit.before });
          continue;
        }
        const existing = map.get(key);
        map.set(key, {
          tenantId,
          concretePath: path,
          value: edit.after,
          original: existing ? existing.original : edit.before,
          pending: edit,
        });
      }
    }
    return { entries: [...map.values()], removedEntries: removed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, index, state.version]);

  const definedTenantCount = useMemo(
    () => new Set(entries.map((e) => e.tenantId)).size,
    [entries],
  );

  // Tenants with no effective entry. Read views fold pending removals in
  // here too; the edit table shows those as revertable "removed" rows instead.
  const removedTenantIds = useMemo(
    () => new Set(removedEntries.map((e) => e.tenantId)),
    [removedEntries],
  );
  const unsetTenants = useMemo(() => {
    const defined = new Set(entries.map((e) => e.tenantId));
    return [...tenantById.values()]
      .filter((t) => !defined.has(t.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries, tenantById]);
  const editUnsetTenants = useMemo(
    () => unsetTenants.filter((t) => !removedTenantIds.has(t.id)),
    [unsetTenants, removedTenantIds],
  );

  const distinct = useMemo(
    () => (row.secret ? Math.min(entries.length, 1) : new Set(entries.map((e) => JSON.stringify(e.value))).size),
    [entries, row.secret],
  );

  // Edit availability — widget-managed keys and schema-less composite values
  // are read-only here (the per-tenant Configuration tab handles them).
  const firstValue = entries[0]?.value;
  const compositeUnknown =
    row.schema === null && typeof firstValue === 'object' && firstValue !== null;
  const editDisabledReason = row.widget
    ? "Edit per-tenant in the operator's Configuration tab"
    : compositeUnknown
      ? 'Schema-less object/array values are read-only here'
      : null;

  const autoGrouped = distinct <= 8 && distinct <= Math.max(8, entries.length / 3);
  const view = editing
    ? 'table'
    : row.secret
      ? 'grouped'
      : (viewOverride ?? (autoGrouped ? 'grouped' : 'table'));

  const hasWildcard = row.id.includes('[*]');

  const groups = useMemo(() => {
    if (view !== 'grouped') return [];
    const byValue = new Map<string, { value: unknown; items: ValueGroupItem[] }>();
    for (const entry of entries) {
      const groupKey = row.secret ? 'set' : JSON.stringify(entry.value) ?? 'undefined';
      let group = byValue.get(groupKey);
      if (!group) {
        group = { value: entry.value, items: [] };
        byValue.set(groupKey, group);
      }
      group.items.push({
        key: `${entry.tenantId}:${entry.concretePath}`,
        tenantId: entry.tenantId,
        name: tenantById.get(entry.tenantId)?.name ?? `#${entry.tenantId}`,
        code: tenantById.get(entry.tenantId)?.code ?? null,
        ...(hasWildcard ? { tooltip: entry.concretePath } : {}),
        ...(entry.pending ? { pending: true } : {}),
      });
    }
    return [...byValue.values()].sort((a, b) => b.items.length - a.items.length);
  }, [view, entries, row.secret, tenantById, hasWildcard]);

  const unsetItems = useMemo<ValueGroupItem[]>(
    () =>
      unsetTenants.map((t: TenantConfigItem) => ({
        key: `unset:${t.id}`,
        tenantId: t.id,
        name: t.name,
        code: t.code,
      })),
    [unsetTenants],
  );

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default' }}>
      {/* Header strip — key facts + view/edit controls */}
      <Stack direction="row" spacing={3} sx={{ mb: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            {/* The dotted path is what goes into a ticket or a topconf edit. */}
            <ValueActions actions={[act.copy(row.key, 'Copy key path')]}>{row.key}</ValueActions>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {row.type}
            {row.secret ? ' · secret' : ''} · defined by {definedTenantCount} of {tenantCount}{' '}
            tenants{row.secret ? '' : ` · ${distinct} distinct value${distinct === 1 ? '' : 's'}`}
          </Typography>
          {row.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {row.description}
            </Typography>
          )}
          {row.schema?.default !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Default:{' '}
              <ValueActions actions={[act.copy(JSON.stringify(row.schema.default), 'Copy default value')]}>
                <code>{safePreview(row.schema.default, 80)}</code>
              </ValueActions>
              {row.schema['x-default-source'] && ` (from ${row.schema['x-default-source']})`}
            </Typography>
          )}
        </Box>
        {!row.secret && !editing && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_e, v: 'grouped' | 'table' | null) => v && setViewOverride(v)}
          >
            <ToggleButton value="grouped">Grouped</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
          </ToggleButtonGroup>
        )}
        <PermissionGate perm="console_top_manage">
          <Tooltip title={editDisabledReason ?? ''}>
            <span>
              <Button
                size="small"
                variant={editing ? 'contained' : 'outlined'}
                startIcon={<EditIcon />}
                disabled={editDisabledReason !== null}
                onClick={() => setEditing((e) => !e)}
              >
                {editing ? 'Done' : 'Edit values'}
              </Button>
            </span>
          </Tooltip>
        </PermissionGate>
      </Stack>

      {editing && removedEntries.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {removedEntries.length} pending removal{removedEntries.length === 1 ? '' : 's'} — struck
          rows below revert individually.
        </Alert>
      )}

      {view === 'grouped' ? (
        <Box>
          {groups.map((group, i) => (
            <ValueGroupSection
              key={i}
              title={
                row.secret
                  ? `Set (masked) — ${group.items.length} tenant${group.items.length === 1 ? '' : 's'}`
                  : `${safePreview(group.value, 80)} — ${group.items.length} tenant${group.items.length === 1 ? '' : 's'}`
              }
              items={group.items}
            />
          ))}
          {unsetItems.length > 0 && (
            <ValueGroupSection
              title={`(unset) — ${unsetItems.length} tenant${unsetItems.length === 1 ? '' : 's'}`}
              items={unsetItems}
              collapsedByDefault={unsetItems.length > 20}
            />
          )}
        </Box>
      ) : (
        <TenantValueTable
          row={row}
          entries={entries}
          removedEntries={removedEntries}
          unsetTenants={editing ? editUnsetTenants : unsetTenants}
          editing={editing}
        />
      )}

    </Box>
  );
}
